<template>
  <teleport to="body">
    <transition name="modal">
      <div v-if="isOpen" class="review-modal-overlay" @click.self="close">
        <div class="review-modal-container">
          <div class="review-modal-header">
            <h4 class="review-modal-title">
              <i class="bi bi-star-fill me-2"></i>
              Γράψτε την κριτική σας
            </h4>
            <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
          </div>
          
          <div class="review-modal-body">
            <form @submit.prevent="submitReview">
              <!-- Name Field -->
              <div class="mb-3">
                <label for="reviewName" class="form-label">
                  Όνομα <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="reviewName"
                  v-model="formData.name"
                  placeholder="Το όνομά σας"
                  required
                  :disabled="submitting"
                />
              </div>
              
              <!-- Rating Field (Optional) -->
              <div class="mb-3">
                <label class="form-label">Αξιολόγηση (προαιρετικό)</label>
                <div class="star-rating">
                  <button
                    v-for="star in 5"
                    :key="star"
                    type="button"
                    class="star-button"
                    :class="{ active: star <= formData.rating }"
                    @click="formData.rating = star"
                    :disabled="submitting"
                  >
                    <i class="bi" :class="star <= formData.rating ? 'bi-star-fill' : 'bi-star'"></i>
                  </button>
                  <span v-if="formData.rating" class="ms-2 text-muted">
                    ({{ formData.rating }}/5)
                  </span>
                </div>
              </div>
              
              <!-- Comment Field -->
              <div class="mb-3">
                <label for="reviewComment" class="form-label">
                  Σχόλιο <span class="text-danger">*</span>
                </label>
                <textarea
                  class="form-control"
                  id="reviewComment"
                  v-model="formData.comment"
                  rows="5"
                  placeholder="Περιγράψτε την εμπειρία σας..."
                  required
                  minlength="10"
                  :disabled="submitting"
                ></textarea>
                <div class="form-text">Τουλάχιστον 10 χαρακτήρες</div>
              </div>
              
              <!-- Photo Upload -->
              <div class="mb-3">
                <label for="reviewPhoto" class="form-label">
                  Φωτογραφία (προαιρετικό)
                </label>
                <input
                  type="file"
                  class="form-control"
                  id="reviewPhoto"
                  @change="handlePhotoUpload"
                  accept="image/*"
                  :disabled="submitting"
                />
                <div class="form-text">JPG, PNG, GIF, WebP - Μέγιστο 5MB</div>
                
                <!-- Photo Preview -->
                <div v-if="photoPreview" class="photo-preview mt-2">
                  <img :src="photoPreview" alt="Preview" class="img-thumbnail" />
                  <button
                    type="button"
                    class="btn btn-sm btn-danger remove-photo"
                    @click="removePhoto"
                    :disabled="submitting"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>
              
              <!-- Date Field -->
              <div class="mb-3">
                <label for="reviewDate" class="form-label">
                  Ημερομηνία <span class="text-danger">*</span>
                </label>
                <input
                  type="date"
                  class="form-control"
                  id="reviewDate"
                  v-model="formData.reviewDate"
                  :max="todayDate"
                  required
                  :disabled="submitting"
                />
                <div class="form-text">Η ημερομηνία της εμπειρίας σας</div>
              </div>
              
              <!-- Error Message -->
              <div v-if="errorMessage" class="alert alert-danger">
                <i class="bi bi-exclamation-triangle me-2"></i>
                {{ errorMessage }}
              </div>
              
              <!-- Info Message -->
              <div class="alert alert-info">
                <i class="bi bi-info-circle me-2"></i>
                Η κριτική σας θα ελεγχθεί από τον διαχειριστή πριν εμφανιστεί στην ιστοσελίδα.
              </div>
              
              <!-- Submit Button -->
              <div class="d-grid gap-2">
                <button
                  type="submit"
                  class="btn btn-primary btn-lg"
                  :disabled="submitting"
                >
                  <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="bi bi-send me-2"></i>
                  {{ submitting ? 'Υποβολή...' : 'Υποβολή Κριτικής' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import axios from 'axios'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'close'): void
  (e: 'submitted'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Form data
const formData = ref({
  name: '',
  comment: '',
  rating: 0,
  reviewDate: new Date().toISOString().split('T')[0]
})

const photoFile = ref<File | null>(null)
const photoPreview = ref<string | null>(null)
const submitting = ref(false)
const errorMessage = ref('')

// Get today's date for max date validation
const todayDate = computed(() => {
  return new Date().toISOString().split('T')[0]
})

const handlePhotoUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (file) {
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      errorMessage.value = 'Η φωτογραφία είναι πολύ μεγάλη. Μέγιστο μέγεθος: 5MB'
      target.value = ''
      return
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      errorMessage.value = 'Παρακαλώ επιλέξτε μια έγκυρη εικόνα'
      target.value = ''
      return
    }
    
    photoFile.value = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      photoPreview.value = e.target?.result as string
    }
    reader.readAsDataURL(file)
    
    errorMessage.value = ''
  }
}

const removePhoto = () => {
  photoFile.value = null
  photoPreview.value = null
  const fileInput = document.getElementById('reviewPhoto') as HTMLInputElement
  if (fileInput) {
    fileInput.value = ''
  }
}

const submitReview = async () => {
  try {
    submitting.value = true
    errorMessage.value = ''
    
    // Create FormData for multipart upload
    const formDataObj = new FormData()
    formDataObj.append('name', formData.value.name)
    formDataObj.append('comment', formData.value.comment)
    formDataObj.append('review_date', formData.value.reviewDate)
    
    if (formData.value.rating > 0) {
      formDataObj.append('rating', formData.value.rating.toString())
    }
    
    if (photoFile.value) {
      formDataObj.append('photo', photoFile.value)
    }
    
    // Submit to API
    const response = await axios.post('/backend/api/reviews.php', formDataObj, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    
    if (response.data.success) {
      // Reset form
      formData.value = {
        name: '',
        comment: '',
        rating: 0,
        reviewDate: new Date().toISOString().split('T')[0]
      }
      photoFile.value = null
      photoPreview.value = null
      
      // Emit success
      emit('submitted')
    } else {
      errorMessage.value = response.data.error || 'Σφάλμα κατά την υποβολή της κριτικής'
    }
  } catch (error: any) {
    console.error('Error submitting review:', error)
    errorMessage.value = error.response?.data?.error || 'Σφάλμα κατά την υποβολή της κριτικής'
  } finally {
    submitting.value = false
  }
}

const close = () => {
  if (!submitting.value) {
    emit('close')
  }
}
</script>

<style scoped>
.review-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  overflow-y: auto;
}

.review-modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  margin: auto;
}

.review-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.review-modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.review-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.btn-close:hover {
  opacity: 1;
}

/* Star Rating */
.star-rating {
  display: flex;
  align-items: center;
}

.star-button {
  background: none;
  border: none;
  font-size: 1.8rem;
  color: #ddd;
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.2s;
}

.star-button:hover,
.star-button.active {
  color: #ffc107;
}

.star-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Photo Preview */
.photo-preview {
  position: relative;
  display: inline-block;
}

.photo-preview img {
  max-width: 200px;
  max-height: 200px;
  object-fit: cover;
}

.remove-photo {
  position: absolute;
  top: -8px;
  right: -8px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .review-modal-container,
.modal-leave-active .review-modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .review-modal-container,
.modal-leave-to .review-modal-container {
  transform: scale(0.9);
}

/* Responsive */
@media (max-width: 576px) {
  .review-modal-container {
    max-height: 95vh;
  }
  
  .review-modal-header,
  .review-modal-body {
    padding: 1rem;
  }
  
  .review-modal-title {
    font-size: 1.25rem;
  }
}
</style>
